import React, { Component, createRef } from 'react';

import { styled as compiledStyled } from '@compiled/react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from 'styled-components';
import debounce from 'lodash/debounce';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';

import { layout } from '@atlassian/jira-platform-board-kit';

import {
	HOVER_INTENT_DELAY_MS,
	SHOW_MESSAGE_HEIGHT_LIMIT,
} from '@atlassian/jira-platform-board-kit/src/ui/column/column-transition-zones-container/transition-zone/constants';

import ColumnTransitionLabel from '@atlassian/jira-platform-board-kit/src/ui/column/column-transition-zones-container/transition-zone/label';
import type {
	ColumnTransitionZoneProps,
	Props,
} from '@atlassian/jira-platform-board-kit/src/ui/column/column-transition-zones-container/transition-zone/types';

type State = {
	preferredHeight: number;
};

// eslint-disable-next-line jira/react/no-class-components
export class ColumnTransitionZoneOld extends Component<ColumnTransitionZoneProps, State> {
	static defaultProps = {
		LabelComponent: ColumnTransitionLabel,
	};

	state = {
		preferredHeight: 0,
	};

	componentWillUnmount() {
		this.debouncedHoverIntent.cancel();
	}

	zoneRef = createRef<HTMLDivElement>();

	handleCardDrag = (hoverIntent: boolean) => {
		const { onDragOver, transitionId, isTransitionAvailable, isHidden } = this.props;
		if (isHidden) {
			return;
		}
		if (isTransitionAvailable) {
			onDragOver(transitionId, hoverIntent);
		} else {
			onDragOver(null, false);
		}
	};

	handleCardDragHoverIntent = () => this.handleCardDrag(true);

	handleMouseLeave = () => {
		this.debouncedHoverIntent.cancel();
	};

	handleMouseEnter = () => {
		this.handleCardDrag(false);
	};

	debouncedHoverIntent = debounce(this.handleCardDragHoverIntent, HOVER_INTENT_DELAY_MS);

	handleMouseUpEvent = (e: React.MouseEvent) => {
		const { transitionId, isHidden, isTransitionAvailable, onTransitionSelection } = this.props;
		if (isHidden || !isTransitionAvailable) {
			return;
		}

		// Stop event bubbling up the parent container as we have prop to handle
		// Also parent container has separate mouseUp Event handler for other scenario
		e.stopPropagation();
		onTransitionSelection(transitionId);
	};

	render() {
		const {
			targetStatusName,
			transitionName,
			targetStatusCategory,
			sourceStatus,
			isTransitionAvailable,
			isTransitionGlobal,
			notAvailableMessage,
			LabelComponent,
			hideSourceStatusInLabel,
		} = this.props;

		const { preferredHeight } = this.state;

		const labelEl = LabelComponent ? (
			<LabelComponent
				source={hideSourceStatusInLabel ? null : transitionName}
				sourceStatus={hideSourceStatusInLabel ? null : sourceStatus}
				target={targetStatusName}
				targetStatusCategory={targetStatusCategory}
				isTransitionAvailable={isTransitionAvailable}
				isTransitionGlobal={isTransitionGlobal}
				showMessage={preferredHeight > SHOW_MESSAGE_HEIGHT_LIMIT}
				notAvailableMessage={notAvailableMessage}
			/>
		) : null;
		const commonProps = {
			onMouseEnter: this.handleMouseEnter,
			onMouseLeave: this.handleMouseLeave,
			onMouseMove: this.debouncedHoverIntent,
			isTransitionAvailable,
			onMouseUp: this.handleMouseUpEvent,
		};

		return (
			<CompiledTransitionZonesContainer
				ref={this.zoneRef}
				{...commonProps}
				data-testid="platform-board-kit-legacy-dnd.ui.column.column-transition-zones-container.transition-zone.compiled-transition-zones-container"
			>
				{labelEl}
			</CompiledTransitionZonesContainer>
		);
	}
}

export default ColumnTransitionZoneOld;

const borderWidth = 2;
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const borderRadius = `${layout.cardListRadius}px`;

const getAvailableBorderColour = (props: Props) =>
	props.isDraggingOver
		? token('color.border.brand', colors.B300)
		: token('color.border.focused', colors.B200);
const getDraggingBackgroundColour = (props: Props) =>
	props.isDraggingOver
		? token('color.background.selected.hovered', colors.B75)
		: token('color.background.selected', colors.B50);

const getBorder = (props: Props) => ({
	border: `${borderWidth}px solid ${
		!props.isTransitionAvailable
			? token('color.background.accent.red.subtler', colors.R75)
			: getAvailableBorderColour(props)
	}`,
});

const getBackgroundColor = (props: Props) => ({
	backgroundColor: `${
		!props.isTransitionAvailable
			? token('color.background.danger', colors.R50)
			: getDraggingBackgroundColour(props)
	}`,
});

// eslint-disable-next-line jira/compiled/styled-components-usages, jira/styled/no-styled-export, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
export const StyledTransitionZonesContainer = styled.div<Props>((props) => ({
	flex: '1 1 auto',
	flexDirection: 'column',
	minHeight: 0,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	position: 'relative',
	padding: token('space.075', '6px'),
	pointerEvents: 'all',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...getBackgroundColor(props),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:first-of-type': {
		borderRadius: `${borderRadius} ${borderRadius} 0 0`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:last-child': {
		borderRadius: `0 0 ${borderRadius} ${borderRadius}`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:only-child': {
		borderRadius,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:not(:last-child)': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		borderBottom: props.isDraggingOver ? 'auto' : 'none',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...(props.isDraggingOver && { '+ div': { borderTop: 'none' } }),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...getBorder(props),
}));

// eslint-disable-next-line jira/styled/no-styled-export, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const CompiledTransitionZonesContainer = compiledStyled.div<Props>({
	flex: '1 1 auto',
	flexDirection: 'column',
	minHeight: 0,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	position: 'relative',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	padding: `${layout.smallGutter}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	border: (props) =>
		`${borderWidth}px solid ${
			!props.isTransitionAvailable
				? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					token('color.background.accent.red.subtler', colors.R75)
				: getAvailableBorderColour(props)
		}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: (props) =>
		!props.isTransitionAvailable
			? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				token('color.background.danger', colors.R50)
			: getDraggingBackgroundColour(props),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	pointerEvents: 'all !important',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:first-of-type': {
		borderRadius: `${borderRadius} ${borderRadius} 0 0`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:last-child': {
		borderRadius: `0 0 ${borderRadius} ${borderRadius}`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:only-child': {
		borderRadius,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'+ div': {
		borderTop: 'none',
	},
});
